import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { COMPANY } from "@/data/site";

const Block = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-glass mb-4">{title}</h2>
    <div className="text-white/70 font-light leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="datenschutz-page">
      <header className="glass-strong border-b border-white/10">
        <div className="mx-auto max-w-4xl px-6 md:px-10 h-20 flex items-center gap-4">
          <Link to="/" data-testid="datenschutz-back" className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/10 hover:border-cyan-glass hover:text-cyan-glass transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="flex items-center gap-3">
            <img src={logo} alt="S&P Reinigungsservice" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/20" />
            <span className="font-heading text-lg font-semibold tracking-tight">S&amp;P Reinigungsservice</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 md:px-10 py-16 md:py-24">
        <h1 className="font-heading text-5xl md:text-7xl font-semibold tracking-tighter mb-4">Datenschutz&shy;erklärung</h1>
        <p className="text-white/50 font-light mb-16">Informationen gemäß Art. 13 &amp; 14 DSGVO</p>

        <Block title="1. Verantwortlicher">
          <p className="text-white">{COMPANY.name}</p>
          <p>{COMPANY.street}, {COMPANY.zip}</p>
          <p>Telefon: <a href={`tel:${COMPANY.phoneRaw}`} className="text-white hover:text-cyan-glass transition-colors">{COMPANY.phone}</a></p>
          <p>E-Mail: <a href={`mailto:${COMPANY.email}`} className="text-white hover:text-cyan-glass transition-colors break-all">{COMPANY.email}</a></p>
        </Block>

        <Block title="2. Allgemeines zur Datenverarbeitung">
          <p>
            Wir verarbeiten personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit dies zur
            Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen
            erforderlich ist. Die Verarbeitung erfolgt regelmäßig nur nach Einwilligung der Nutzer
            (Art. 6 Abs. 1 lit. a DSGVO), zur Erfüllung eines Vertrags bzw. vorvertraglicher Maßnahmen
            (Art. 6 Abs. 1 lit. b DSGVO) oder zur Wahrung berechtigter Interessen
            (Art. 6 Abs. 1 lit. f DSGVO).
          </p>
        </Block>

        <Block title="3. Aufruf der Website & Server-Logfiles">
          <p>
            Beim Besuch unserer Website werden durch den Hosting-Anbieter automatisch Informationen
            erfasst, die Ihr Browser übermittelt (Server-Logfiles): Browsertyp und -version,
            verwendetes Betriebssystem, Referrer-URL, Hostname des zugreifenden Rechners, Uhrzeit der
            Serveranfrage sowie die IP-Adresse. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Diese
            Daten dienen der technischen Bereitstellung, Sicherheit und Stabilität der Website und
            werden nicht mit anderen Datenquellen zusammengeführt.
          </p>
        </Block>

        <Block title="4. Kontakt- und Angebotsformular">
          <p>
            Wenn Sie uns über das Angebotsformular kontaktieren, verarbeiten wir die von Ihnen
            angegebenen Daten (Name, E-Mail-Adresse, Telefonnummer, gewünschte Leistung sowie Ihre
            Nachricht), um Ihre Anfrage zu bearbeiten und Ihnen ein Angebot zu erstellen.
          </p>
          <p>
            Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen) sowie
            Art. 6 Abs. 1 lit. f DSGVO (Bearbeitung von Anfragen). Die Daten werden gelöscht, sobald
            sie für die Erreichung des Zwecks nicht mehr erforderlich sind, bzw. gesetzliche
            Aufbewahrungsfristen abgelaufen sind.
          </p>
        </Block>

        <Block title="5. Hosting">
          <p>
            Diese Website wird bei einem externen Dienstleister gehostet. Die auf dieser Website
            erfassten Daten werden auf den Servern des Hosters gespeichert. Der Einsatz des Hosters
            erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren potenziellen und bestehenden
            Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer sicheren, schnellen und
            effizienten Bereitstellung unseres Online-Angebots (Art. 6 Abs. 1 lit. f DSGVO). Mit dem
            Hoster wurde ein Vertrag zur Auftragsverarbeitung (AVV) geschlossen.
          </p>
        </Block>

        <Block title="6. Schriftarten (Google Fonts & Fontshare)">
          <p>
            Zur einheitlichen Darstellung von Schriftarten nutzt diese Website Web-Schriften, die von
            Google (Google Fonts) und Fontshare bereitgestellt werden. Beim Aufruf einer Seite lädt
            Ihr Browser die benötigten Schriften, um Texte korrekt anzuzeigen. Hierzu muss Ihr Browser
            eine Verbindung zu den Servern der Anbieter aufbauen, wodurch diese Kenntnis von Ihrer
            IP-Adresse erlangen. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (einheitliche,
            ansprechende Darstellung).
          </p>
        </Block>

        <Block title="7. Reichweitenmessung / Analyse">
          <p>
            Zur Verbesserung unseres Angebots kann eine anonymisierte bzw. pseudonymisierte
            Reichweitenmessung eingesetzt werden. Dabei werden Nutzungsdaten (z. B. aufgerufene Seiten)
            verarbeitet, um die Website nutzerfreundlicher zu gestalten. Rechtsgrundlage ist
            Art. 6 Abs. 1 lit. f DSGVO bzw. Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), sofern
            erforderlich. Sie können der Verarbeitung jederzeit widersprechen.
          </p>
        </Block>

        <Block title="8. SSL-/TLS-Verschlüsselung">
          <p>
            Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher
            Inhalte eine SSL-/TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie an
            „https://" in der Adresszeile Ihres Browsers.
          </p>
        </Block>

        <Block title="9. Ihre Rechte als betroffene Person">
          <p>Ihnen stehen nach der DSGVO folgende Rechte zu:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
            <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
            <li>Recht auf Löschung (Art. 17 DSGVO)</li>
            <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
            <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
            <li>Widerspruchsrecht (Art. 21 DSGVO)</li>
            <li>Recht auf Widerruf einer erteilten Einwilligung (Art. 7 Abs. 3 DSGVO)</li>
          </ul>
          <p>
            Zur Ausübung Ihrer Rechte genügt eine formlose Mitteilung an die oben genannten
            Kontaktdaten.
          </p>
        </Block>

        <Block title="10. Beschwerderecht bei der Aufsichtsbehörde">
          <p>
            Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Zuständig
            ist:
          </p>
          <p className="text-white">Die Landesbeauftragte für den Datenschutz Niedersachsen</p>
          <p>Prinzenstraße 5, 30159 Hannover — www.lfd.niedersachsen.de</p>
        </Block>

        <p className="text-white/30 text-sm font-mono mt-16 pt-8 border-t border-white/10">
          Stand: Dezember 2025 · Diese Datenschutzerklärung ersetzt keine Rechtsberatung.
        </p>
      </main>
    </div>
  );
}
